import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { apiClient, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

const isDev = process.env.NODE_ENV !== "production";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = anonymous
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
    } catch (e) {
      // 401 on first visit is expected (anonymous session); deeper errors only logged in dev
      if (isDev && e?.response?.status && e.response.status !== 401) {
        // eslint-disable-next-line no-console
        console.error("Auth check failed:", e);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await apiClient.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await apiClient.post("/auth/register", payload);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch (e) {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.error("Logout request failed:", e);
      }
    }
    setUser(false);
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh: fetchMe }),
    [user, loading, fetchMe]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
